import React from 'react';

import { Bars3Icon } from '@heroicons/react/24/outline';

import { Interactive, Row } from 'components';

import { useAppShellStore } from './AppShell';

import { cn } from 'domain/core';

interface NavbarProps extends React.PropsWithChildren {
	className?: string;
}
/**
 * App Navbar. Can be used in conjunction with AppShell.Header
 * @param props
 * @returns
 */
const Navbar = (props: NavbarProps) => {
	return (
		<nav
			className={cn(
				'navbar',
				'h-full w-full',
				'flex flex-row flex-wrap items-center justify-start',
				'bg-primary-500',
				'md:flex-nowrap md:overflow-hidden',
				'z-50 border-b',
				props.className
			)}>
			{props.children}
		</nav>
	);
};

interface BrandProps extends React.PropsWithChildren {
	className?: string;
}
/**
 * App logo or name container. Shown on mobile & desktop
 */
const Brand = (props: BrandProps) => {
	return (
		<div
			className={cn(
				'navbar-brand',
				'h-auto w-full px-4 py-2 md:w-auto',
				'flex flex-row items-center justify-start gap-4 text-white',
				props.className
			)}>
			{props.children}
		</div>
	);
};

interface ToggleProps extends React.PropsWithChildren {
	className?: string;
}
/**
 * Component responsible for showing or hiding the content inside Navbar.Collapse
 * Only visible on mobile
 * @param props
 * @returns
 */
const Toggle = (props: ToggleProps) => {
	const { toggleNavbar } = useAppShellStore();

	const onClick = () => {
		toggleNavbar();
	};

	return (
		<Interactive onInteract={onClick}>
			<Row
				className={cn(
					'navbar-toggle',
					'h-[60px] !w-12',
					'mr-4',
					'flex flex-grow-0 flex-row items-center !justify-end gap-0',
					'md:hidden',
					props.className
				)}>
				{props.children ?? <Bars3Icon className={cn('h-8 w-8')} />}
			</Row>
		</Interactive>
	);
};

interface CollapseProps extends React.PropsWithChildren {
	className?: string;
}
/**
 * Component responsible for rendering the content that will only be visible in
 * the desktop version or when the mobile version is expanded.
 * @param props
 * @returns
 */
const Collapse = (props: CollapseProps) => {
	const { navbarOpen } = useAppShellStore();

	return (
		<div
			className={cn(
				'navbar-collapse',
				'md:flex md:w-auto md:flex-row md:items-center md:justify-end',
				'block h-auto w-full py-2 px-4 transition-all',
				'bg-primary-500',
				'md:bg-transparent',
				'z-10',
				!navbarOpen &&
					'-translate-y-full opacity-0 md:translate-y-0 md:opacity-100',
				navbarOpen && 'translate-y-0 shadow-xl md:shadow-none',
				props.className
			)}>
			<div
				className={cn(
					'navbar-collapse-content',
					'h-full w-full',
					'flex flex-col items-start justify-start gap-3',
					'md:flex-row md:items-center md:justify-end'
				)}>
				{props.children}
			</div>
		</div>
	);
};

interface MainProps extends React.PropsWithChildren {
	className?: string;
}
/**
 * Componente responsable de mostrar el contenido que será siempre visible
 * @param props
 * @returns
 */
const Main = (props: MainProps) => {
	return (
		<Row
			className={cn(
				'z-20 justify-between md:w-auto',
				'border-b border-neutral-300 md:border-0',
				props.className
			)}>
			{props.children}
		</Row>
	);
};

Navbar.Brand = Brand;
Navbar.Toggle = Toggle;
Navbar.Collapse = Collapse;
Navbar.Main = Main;

Navbar.displayName = 'Navbar';

export { Navbar };
